'use client'

/**
 * The house product card — the canvas `.pcard` anatomy verbatim (heart · badge ·
 * media · brand · title link · identifier chip + model · price · deal link ·
 * Compare box). Only the data is live; the heart and the compare box write the
 * same localStorage the header panels read.
 */
import Link from 'next/link'
import dynamic from 'next/dynamic'
import type { WatchRelease } from '@/components/price-watch/product-card-watch'
import { CardImage } from '@/components/brand-kit/card-image'
// By file, not through the barrel: the barrel also exports the offer and hero
// badge sets, which put about 25 kB of unused script on every page with a card.
import { Badge } from '@/components/badges/badge'
import type { HomeProduct } from '@/lib/homepage-2026/types'
import { HeartGlyph } from '@/components/chrome/save-compare-controls'
import { dealLinkName, formatPrice, homeDealCtaLabel, productLinkName } from './format'
import { useSavedCompare, type SavedItem } from './saved-compare'
import { formatReviewCount, ratingAriaLabel, ratingSourceLine, type CardRating } from '@/lib/reviews/card-rating'
import { formatRunningCost, runningCostBasisLine } from '@/lib/energy/card-running-cost'
import { heroImageFor } from '@/lib/homepage-2026/hero-images'

// FRIDGE-806 / FRIDGE-1315: no page passes `watchPreview` yet, so the held
// Watch price control must not ride in every card's first-load script. It is
// fetched only by a card that actually renders it.
const ProductCardWatch = dynamic(() => import('@/components/price-watch/product-card-watch').then((m) => m.ProductCardWatch))

export function toSavedItem(p: HomeProduct): SavedItem {
  return { id: p.id, fin: p.fin, brand: p.brand, title: p.title, image: p.image, href: p.href, priceCents: p.priceCents }
}

/** `energyChip`: the ENERGY STAR rail's variant — the green leaf chip under the model line instead of the top badge. */
export function ProductCard({ product, priority = false, energyChip = false, watchPreview = false, watchRelease }: { product: HomeProduct; priority?: boolean; energyChip?: boolean; watchPreview?: boolean; watchRelease?: WatchRelease }) {
  const sc = useSavedCompare()
  const saved = sc.isSaved(product.id)
  const compared = sc.isCompared(product.id)
  const badge = energyChip ? product.badges.find((b) => b.type === 'price_drop') : (product.badges.find((b) => b.type === 'energy_star') ?? product.badges.find((b) => b.type === 'price_drop'))
  return (
    <div data-product-image-card className="pcard" data-fin={product.fin}>
      {badge ? (
        <span className="badge" style={{ padding: 0, background: 'transparent', border: 'none' }}>
          <Badge badge={badge} size="sm" className="rounded-full" />
        </span>
      ) : null}
      {/* FRIDGE-1097: a real button, so Enter and Space, focus and the pressed
          state come from the platform instead of a hand-rolled span. */}
      <button
        type="button"
        className="phe"
        style={{ color: saved ? '#dc2626' : undefined }}
        onClick={() => sc.toggleSaved(toSavedItem(product))}
        aria-pressed={saved}
        aria-label={saved ? 'Remove from saved' : 'Save this product'}
      >
        <HeartGlyph filled={saved} className="i" />
      </button>
      <div className="pmedia">
        {/* FRIDGE-1073 / FRIDGE-1078 §8: a FIN with an approved cutout shows it instead
            of the catalog lead image (FIN-0016877's only catalog photo carries a
            retailer "Craft Ice" banner; its vetted cutout does not). */}
        <CardImage src={heroImageFor(product.fin) ?? product.image} alternate={product.alternateImage} href={product.href} alt={productLinkName(product)} width={220} height={220} priority={priority} />
      </div>
      <span className="pbrand">{product.brand}</span>
      <span className="ptitle">
        <Link className="ptlink" href={product.href} aria-label={productLinkName(product)}>
          {product.title}
        </Link>
      </span>
      <span className="pmodel">
        <span className="idchip">{product.fin}</span>
        {/* FRIDGE-1031: its own flex item so it can ellipsize; the full model stays DOM text and rides the tooltip. */}
        {product.model ? <span className="pmtext" title={`Model ${product.model}`}>{` · Model ${product.model}`}</span> : null}
      </span>
      {product.rating ? <RatingLine rating={product.rating} /> : null}
      <FactLines dimensionsText={product.dimensionsText} energyKwhYr={product.energyKwhYr} />
      {energyChip && product.energyStar ? (
        <span className="eschip">
          <svg className="i" style={{ width: '12px', height: '12px' }}>
            <use href="#i-leaf" />
          </svg>
          ENERGY STAR
        </span>
      ) : null}
      {/* FRIDGE-1078: `.pprow` takes the column's slack (margin-top: auto) so the price, the
          deal button and the Compare box sit on one baseline across a stretched row, whatever
          optional rating or fact lines a card carries above them. */}
      <div className="pprow">
        <span className="pprice">{formatPrice(product.priceCents)}</span>
      </div>
      {/* FRIDGE-814: the rendered price decides the label; the product names its destination. */}
      <Link className="pcta" href={product.href} aria-label={dealLinkName(product)}>
        {homeDealCtaLabel(product.priceCents)}
      </Link>
      {/* Inactive until release acceptance; existing homepage callers omit this opt-in. */}
      {watchPreview ? <ProductCardWatch key={product.id} productId={product.id} productTitle={product.title} release={watchRelease}/> : null}
      <div
        className="pcompare"
        role="checkbox"
        aria-checked={compared}
        tabIndex={0}
        onClick={() => sc.toggleCompare(toSavedItem(product))}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            sc.toggleCompare(toSavedItem(product))
          }
        }}
      >
        <span className="box" style={compared ? { background: '#000000', borderColor: '#000000' } : undefined} />
        Compare
      </div>
    </div>
  )
}

/**
 * FRIDGE-786 — "★★★★☆ 4.3 (2,182)": the cross-source retailer rating the
 * product page shows, rendered only when lib/reviews/card-rating.ts says the
 * evidence supports it. The five glyphs are one `role="img"` whose name states
 * the rating, the count AND that they are retailer reviews (never staff
 * testing); the numeral and count repeat it visually and are hidden from AT.
 * The source names ride the tooltip so the provenance is one hover away.
 */
function RatingLine({ rating }: { rating: CardRating }) {
  const fill = `${Math.max(0, Math.min(100, (rating.rating / 5) * 100))}%`
  return (
    <span className="prating" title={ratingSourceLine(rating)}>
      {/* The star glyphs are CSS pseudo-content (decorative text is not DOM text); the fill width is the only inline value. */}
      <span className="pstars" role="img" aria-label={ratingAriaLabel(rating)}><i className="pstars-fill" style={{ width: fill }} /></span>
      <span className="pscore" aria-hidden="true">{rating.rating.toFixed(1)}</span>
      <span className="pcount" aria-hidden="true">({formatReviewCount(rating.reviews)})</span>
    </span>
  )
}

/**
 * FRIDGE-802 — the two fact lines under the identifier: verified dimensions
 * in the site's labelled order (W × H × D) and a labelled running-cost
 * estimate. Each line renders only when the catalog supports it; a card with
 * neither renders nothing extra, so no layout is reserved for a guess.
 */
function FactLines({ dimensionsText, energyKwhYr }: { dimensionsText?: string | null; energyKwhYr?: number | null }) {
  const cost = formatRunningCost(energyKwhYr)
  if (!dimensionsText && !cost) return null
  return (
    <span className="pfacts">
      {dimensionsText ? <span className="pdims">{dimensionsText}</span> : null}
      {cost ? (
        <span className="pcost" title={runningCostBasisLine(energyKwhYr)}>
          {cost}
          <span className="sr-only"> per year, estimated</span>
        </span>
      ) : null}
    </span>
  )
}
